import { NgComponentOutlet } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  effect,
  EffectRef,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  OutputRefSubscription,
  signal,
  ViewChild,
  Type,
  ViewContainerRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BackButtonComponent,
  ButtonComponent,
  CardComponent,
  LoadingOverlayComponent,
  ModalComponent,
  StepComponent,
  StepperComponent,
  ToastService,
} from '@webapp/ui';
import { RiskLoaderComponent } from '../../components/risk-loader/risk-loader.component';
import { QuoterStoreService, PROSPECT_SIGNUP_STEP_ID } from '../../../store/quoter-store.service';
import { QuoterInsurableService } from '../../../services/quoter-insurable.service';
import { IUser, USER_TYPES } from '@webapp/users/interfaces/users.interface';
import {
  AuthService,
  CodeVerificationFormComponent,
  EmailVerificationService,
  extractErrorMessage,
  ICodeVerificationEvent,
  OTP_LOGIN_MESSAGES,
} from '@webapp/auth';
import { CreateUserFormComponent } from '../../components/create-user-form/create-user-form.component';
import { QuoterPaymentComponent } from '../../components/quoter-payment/quoter-payment.component';
import { QuoterSidebarComponent } from '../../components/quoter-sidebar/quoter-sidebar.component';
import { SearchUsersComponent } from '../../components/search-users/search-users.component';
import { CHANNEL, CHANNEL_QUOTE_ENUM } from '@webapp/core/config';
import { ShowForChannelDirective } from '@webapp/core/directives/showForChannel';
import { RiskInsurableTypes } from '@webapp/risks/domain/riskInsurables';
import { stampQuoterInsurable } from '../../../common/stamp-quoter-insurable';
import { IRiskQuoterFormComponent } from '@webapp/risks/interfaces/risk-step-component.interface';
import { QuoterCoveragesResultComponent } from '../../components/quoter-coverages-result/quoter-coverages-result';
import { ICoverage } from '@webapp/payments';
import { findRiskTagBySlug } from '../../../risks/risk-slugs';
import { ProspectSignupStepComponent } from '../../components/prospect-signup-step/prospect-signup-step.component';
import { firstValueFrom } from 'rxjs';
import { OnboardingSeenService } from '@webapp/core';
import { IRiskOnboardingComponent } from '@webapp/risks';
import { WEB_RISK_SLOTS } from '@webapp/risks/web';
import { RiskPlugin, RiskRegistry, RiskUiSlot } from '@webapp/risk-core';
import { QUOTER_RISK_SLOTS } from '../../../config/provide-quoter-risk-ui';
@Component({
  selector: 'app-quoter-base',
  imports: [
    ShowForChannelDirective,
    StepperComponent,
    StepComponent,
    CardComponent,
    SearchUsersComponent,
    ButtonComponent,
    CreateUserFormComponent,
    BackButtonComponent,
    NgComponentOutlet,
    LoadingOverlayComponent,
    RiskLoaderComponent,
    QuoterCoveragesResultComponent,
    QuoterSidebarComponent,
    QuoterPaymentComponent,
    ModalComponent,
    CodeVerificationFormComponent,
    ProspectSignupStepComponent,
  ],
  standalone: true,
  templateUrl: './quoter-base.component.html',
  styleUrl: './quoter-base.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuoterBaseComponent implements OnInit, OnDestroy {
  @ViewChild('dynamicComponentContainer', { read: ViewContainerRef })
  container!: ViewContainerRef;
  @ViewChild('onboardingContainer', { read: ViewContainerRef })
  onboardingContainerRef!: ViewContainerRef;
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  readonly quoterStateService = inject(QuoterStoreService);
  readonly quoterState = this.quoterStateService.state.asReadonly();
  private readonly quoterInsurableService = inject(QuoterInsurableService);
  readonly channel = inject(CHANNEL);
  private readonly emailVerificationService = inject(EmailVerificationService);
  private readonly authService = inject(AuthService);
  private readonly injector = inject(Injector);
  private readonly toastService = inject(ToastService);
  userTypes = signal<string>('');
  userRoles = signal<string>('');
  showLoginModal = signal(false);
  loginLoading = signal(false);
  loginError = signal('');
  pendingCoverage = signal<ICoverage | null>(null);
  readonly PROSPECT_SIGNUP_STEP_ID = PROSPECT_SIGNUP_STEP_ID;
  readonly isInitializing = signal(true);
  readonly loadingRiskTag = signal('');
  private readonly onboardingSeenService = inject(OnboardingSeenService);
  private readonly riskRegistry = inject(RiskRegistry);

  /** Single owner of the current step-2 quoter form instance lifetime. */
  private quoterFormRef: ComponentRef<IRiskQuoterFormComponent> | null = null;
  private quoterFormInputsEffect: EffectRef | null = null;
  private readonly quoterFormSubs: OutputRefSubscription[] = [];
  // Bumped on every teardown so a slow slot chunk cannot mount after a newer build or destroy.
  private quoterFormGeneration = 0;
  // Same guard for the onboarding slot: bumped on every mount request and on destroy.
  private onboardingGeneration = 0;

  async ngOnInit(): Promise<void> {
    this.quoterStateService.initializeState();
    this.quoterInsurableService.clearAll();
    await this.initRisk();

    const { quoteId, coverageId } = this.route.snapshot.queryParams;
    if (quoteId && coverageId) {
      await this.quoterStateService.setQuote(quoteId, coverageId);
      return;
    }

    const authUser = this.authService.user();
    const shouldSkipUserData = this.quoterStateService.shouldSkipUserDataStep(authUser);

    if (this.channel === CHANNEL_QUOTE_ENUM.DIRECT && !shouldSkipUserData) {
      this.quoterStateService.set('isNewUser', true);
    }

    if (this.isQr()) {
      const { qr } = this.route.snapshot.queryParams;
      if (!shouldSkipUserData) {
        this.quoterStateService.set('isNewUser', true);
      }
      this.quoterStateService.setQr(qr).then((success) => {
        if (!success) {
          this.router.navigateByUrl('/');
        }
      });
    }
  }

  private async initRisk() {
    const risk = this.route.snapshot.paramMap.get('risk') ?? '';
    // The route guard only lets known slugs through, so '' is unreachable in practice.
    const riskType = findRiskTagBySlug(this.riskRegistry, risk) ?? '';
    this.loadingRiskTag.set(riskType || risk);
    try {
      await this.quoterStateService.setRisk(riskType);
      const personTypes = this.quoterState().risk.personTypes;

      if (personTypes && personTypes.length) {
        const userType = personTypes.includes('person') && personTypes.includes('company')
          ? ''
          : personTypes[0];
        this.userTypes.set(userType);
      }

      this.userRoles.set('');
      this.quoterStateService.setStepsForRisk(riskType);

      const authUser = this.authService.user();
      if (this.quoterStateService.shouldSkipUserDataStep(authUser) && authUser) {
        this.quoterStateService.set('user', {
          ...authUser,
          isClient: true,
          type: authUser.type?.toUpperCase(),
        });
        this.quoterStateService.set('isNewUser', false);
        this.quoterStateService.updateStepProperties(1, { completed: true });
        this.quoterStateService.updateStepProperties(2, { disabled: false, active: true });
        this.quoterStateService.setActiveStep(2);
        this.quoterStateService.removeStep(1);
        afterNextRender(() => this.buildInsurableComponent(), { injector: this.injector });
      }

      this.mountOnboarding();
    } finally {
      this.isInitializing.set(false);
    }
  }

  async setCoverage(coverage: ICoverage): Promise<void> {
    const user = this.quoterState().user;
    const isDirect = this.channel === CHANNEL_QUOTE_ENUM.DIRECT;

    if (isDirect && user.isClient) {
      if (this.authService.user()) {
        this._applyCoverage(coverage);
        return;
      }
      this.pendingCoverage.set(coverage);
      this.loginLoading.set(true);
      try {
        await firstValueFrom(this.emailVerificationService.sendLoginCode({ email: user.email! }));
        this.openLoginModal();
      } catch (error) {
        console.error('Error al enviar el código:', error);
        this.toastService.showToast(
          'Error',
          extractErrorMessage(error, OTP_LOGIN_MESSAGES.SEND_CODE_ERROR),
          'error'
        );
      } finally {
        this.loginLoading.set(false);
      }
      return;
    }

    if (isDirect && !user.isClient) {
      this.pendingCoverage.set(coverage);
      this.quoterStateService.advanceToProspectSignupStep();
      return;
    }

    this._applyCoverage(coverage);
  }

  async handleProspectSignupSuccess(user: IUser): Promise<void> {
    this.quoterStateService.set('user', { ...user, isClient: false });
    this.loginLoading.set(true);
    try {
      await firstValueFrom(this.emailVerificationService.sendLoginCode({ email: user.email! }));
      this.openLoginModal();
    } catch (error) {
      console.error('Error al enviar el código:', error);
      this.toastService.showToast(
        'Error',
        extractErrorMessage(error, OTP_LOGIN_MESSAGES.SEND_CODE_ERROR),
        'error'
      );
    } finally {
      this.loginLoading.set(false);
    }
  }

  /** Opens the verification modal for a new attempt, clearing any stale
   * inline error left from a previous attempt (D14c). */
  private openLoginModal(): void {
    this.loginError.set('');
    this.showLoginModal.set(true);
  }

  /** The risk inspects the vehicle in-flow (step 4) before issuing. */
  private requiresInspection(): boolean {
    return this.riskRegistry.find(this.quoterState().risk.tag)?.capabilities.requiresInspection ?? false;
  }

  private _applyCoverage(coverage: ICoverage) {
    this.quoterStateService.set('selectedCoverage', coverage);
    const additionalCoverage = this.quoterState().quote.additionalCoverage;

    // Mandatory garage coverage is always part of the policy: no opt-in.
    if (this.riskRegistry.find(this.quoterState().risk.tag)?.capabilities.garage === 'mandatory') {
      additionalCoverage.select = true;
      this.quoterStateService.set('quote', {
        ...this.quoterState().quote,
        additionalCoverage: additionalCoverage,
      });
    }

    if (this.requiresInspection()) {
      this.quoterStateService.advanceToInspectionStep();
      return;
    }

    const state = this.quoterState();
    this.router.navigate(
      ['polizas', 'nueva', state.quote.id],
      {
        queryParams: { coverageId: coverage.id },
        state: { quote: { ...state.quote, risk: state.risk, user: state.user } },
      }
    );
  }

  async handleCodeSubmit(event: ICodeVerificationEvent): Promise<void> {
    this.loginLoading.set(true);
    this.loginError.set('');
    try {
      const res = await firstValueFrom(this.emailVerificationService.verifyLoginCode({
        email: this.quoterState().user.email!,
        code: event.code,
      }));
      const success = this.authService.storeTokensFromResponse(res);
      this.authService.scheduleTokenRefresh(success.data.token);
      const tokenUser = this.authService.user();
      if (tokenUser) {
        this.quoterStateService.set('user', { ...tokenUser, isClient: tokenUser.role !== 'PROSPECT', type: tokenUser.type?.toUpperCase() });
      }
      this.showLoginModal.set(false);
      this._applyCoverage(this.pendingCoverage()!);
      this.pendingCoverage.set(null);
    } catch (error) {
      console.error('Error al verificar el código:', error);
      this.loginError.set(extractErrorMessage(error, OTP_LOGIN_MESSAGES.VERIFY_CODE_ERROR));
    } finally {
      this.loginLoading.set(false);
    }
  }

  async resendLoginCode(): Promise<void> {
    this.loginError.set('');
    try {
      await firstValueFrom(this.emailVerificationService.sendLoginCode({ email: this.quoterState().user.email! }));
    } catch (error) {
      console.error('Error al reenviar el código:', error);
      this.loginError.set(extractErrorMessage(error, OTP_LOGIN_MESSAGES.RESEND_CODE_ERROR));
    }
  }

  closeLoginModal() {
    this.showLoginModal.set(false);
  }

  /**
   * Destroys the previous step-2 quoter form instance (effect, output
   * subscriptions, ComponentRef) before a new one is created. Idempotent —
   * safe to call repeatedly (e.g. from both `buildInsurableComponent()` and
   * `ngOnDestroy()`). This is the fix for the effect/ComponentRef leak that
   * previously happened on every repeated `goToInsurableStep()` invocation.
   */
  private teardownQuoterForm(): void {
    this.quoterFormGeneration++;
    this.quoterFormInputsEffect?.destroy();
    this.quoterFormInputsEffect = null;
    this.quoterFormSubs.forEach((sub) => sub.unsubscribe());
    this.quoterFormSubs.length = 0;
    this.quoterFormRef?.destroy();
    this.quoterFormRef = null;
  }

  ngOnDestroy(): void {
    this.onboardingGeneration++;
    this.teardownQuoterForm();
  }

  private applyQuoterFormInputs(ref: ComponentRef<IRiskQuoterFormComponent>, plugin: RiskPlugin | null): void {
    const state = this.quoterState();
    const extras = plugin?.quoter.formInputs?.({ riskId: state.risk.id, qrToken: state.qr?.token }) ?? {};
    for (const [name, value] of Object.entries(extras)) {
      ref.setInput(name, value);
    }
    // Universal input, applied last so a strategy's extras cannot shadow it.
    ref.setInput('loading', state.submitQuoteLoading);
  }

  buildInsurableComponent(): Promise<void> | void {
    this.teardownQuoterForm();

    const state = this.quoterState();
    const riskType = state.risk.tag;
    const plugin = this.riskRegistry.find(riskType);

    const slot = this.riskRegistry.slot('web', riskType, QUOTER_RISK_SLOTS.QUOTER_FORM);
    if (!slot) {
      console.error(`Component not found (risk: '${riskType}', no quoter-form slot)`);
      return;
    }
    return this.buildInsurableComponentFromSlot(slot, plugin, riskType);
  }

  private async buildInsurableComponentFromSlot(slot: RiskUiSlot, plugin: RiskPlugin | null, riskType: string): Promise<void> {
    const generation = this.quoterFormGeneration;
    try {
      const type = (await slot.load()) as Type<unknown>;
      if (generation !== this.quoterFormGeneration) return;
      this.wireQuoterForm(this.container.createComponent(type) as unknown as ComponentRef<IRiskQuoterFormComponent>, plugin, riskType);
    } catch (error) {
      console.error(`Error loading quoter form slot (risk: '${riskType}'): ${error}`);
    }
  }

  private wireQuoterForm(ref: ComponentRef<IRiskQuoterFormComponent>, plugin: RiskPlugin | null, key: string): void {
    const user = this.quoterState().user;
    this.quoterFormRef = ref;

    // (1) synchronous pass — required inputs (e.g. moto/quad's `riskId`) must
    // be set before the component's first change-detection cycle.
    this.applyQuoterFormInputs(ref, plugin);

    // (2) reactive pass — exactly one effect, owned and destroyed by `teardownQuoterForm()`.
    this.quoterFormInputsEffect = effect(() => this.applyQuoterFormInputs(ref, plugin), { injector: this.injector });

    // (3) universal output, wired unconditionally for every quoter form component.
    this.quoterFormSubs.push(
      ref.instance.formSubmit.subscribe((insurable: RiskInsurableTypes) => {
        this.quote(stampQuoterInsurable(insurable, user));
      })
    );

    // (4) declarative per-risk outputs.
    const instance = ref.instance as unknown as Record<string, { subscribe: (cb: (value: unknown) => void) => OutputRefSubscription } | undefined>;
    for (const binding of plugin?.quoter.formOutputBindings ?? []) {
      const emitter = instance[binding.output];
      if (!emitter?.subscribe) {
        console.warn(`Quoter form '${key}' has no output '${binding.output}'`);
        continue;
      }
      this.quoterFormSubs.push(
        emitter.subscribe((value) => this.quoterStateService.set(binding.stateKey as never, value as never))
      );
    }
  }

  private mountOnboarding(): void {
    const riskTag = this.quoterState().risk.tag;
    const slot = this.riskRegistry.slot('web', riskTag, WEB_RISK_SLOTS.ONBOARDING);
    if (!slot) return;
    if (this.onboardingSeenService.hasSeen(riskTag)) return;

    const generation = ++this.onboardingGeneration;
    afterNextRender(() => void this.loadOnboarding(slot, riskTag, generation), { injector: this.injector });
  }

  private async loadOnboarding(slot: RiskUiSlot, riskTag: string, generation: number): Promise<void> {
    try {
      const type = (await slot.load()) as Type<IRiskOnboardingComponent>;
      if (generation !== this.onboardingGeneration) return;
      this.onboardingContainerRef.clear();
      const ref = this.onboardingContainerRef.createComponent(type);
      ref.setInput('riskTag', riskTag);
      ref.instance.dismissed.subscribe(() => this.onboardingContainerRef.clear());
    } catch (error) {
      console.error(`Error loading onboarding slot (risk: '${riskTag}'): ${error}`);
    }
  }

  quote(insurable: RiskInsurableTypes) {
  
    this.quoterStateService.submitQuote(insurable).then((success) => {
      if (success) {
        this.quoterStateService.setQuoteResultStep();
      }
    });
  }

  showBackButton() {
    return this.channel !== CHANNEL_QUOTE_ENUM.DIRECT;
  }

  isQr(): boolean {
    const params = this.route.snapshot.queryParams;
    return !!params['qr'];
  }

  getDynamicComponent() {
    return this.quoterStateService.dynamicRiskComponent();
  }

  getStepName() {
    return this.quoterStateService.stepName();
  }

  isPaymentStep() {
    return this.quoterStateService.isPaymentStep();
  }

  showDynamicSteps(): boolean {
    const baseSteps = this.quoterStateService.baseSteps;
    return this.quoterState().steps.some(
      (step) => !baseSteps.find((base) => base.id === step.id)
    );
  }

  toggleCreateUserForm() {
    const isNewUser = this.quoterStateService.select('isNewUser');
    this.quoterStateService.set('isNewUser', !isNewUser());
  }

  goToInsurableStep() {
    this.quoterStateService.updateStepProperties(1, { completed: true });
    this.quoterStateService.updateStepProperties(2, { disabled: false, active: true });
    this.quoterStateService.setActiveStep(2);
    this.buildInsurableComponent()
  }

  setUser(user: IUser) {
    user.type = user.type?.toUpperCase();
    user.isClient = (user.role !== "PROSPECT") ? true : false;
    this.quoterStateService.set('user', user);
  }

  createUser(user: IUser) {
    if (!user.isClient) user.isClient = false;
    user.address = '';
    user.identification = user.identification ? user.identification : '';
    if (user.type == USER_TYPES.PERSON.toUpperCase()) {
      user.birthDate = '';
    }

    this.quoterStateService.set('user', user);
    
    this.goToInsurableStep();
  }
}
